import { documentsBySlug } from './documents';

let cachedPageDocuments = null;

function getPageDocuments() {
  if (cachedPageDocuments) {
    return cachedPageDocuments;
  }

  const sourceDocuments = [
    require('../pages/AdmissionPage').admissionDocument,
    require('../pages/AssignmentsPage').assignmentsDocument,
    require('../pages/AttendancePage').attendanceDocument,
    require('../pages/CalendarPage').calendarDocument,
    require('../pages/DiscussPage').discussDocument,
    require('../pages/ExamPage').examDocument,
    require('../pages/ExpensesPage').expensesDocument,
    require('../pages/FacultyPage').facultyDocument,
    require('../pages/FleetPage').fleetDocument,
    require('../pages/HRPage').hrDocument,
    require('../pages/InventoryPage').inventoryDocument,
    require('../pages/InvoicePage').invoiceDocument,
    require('../pages/LibraryPage').libraryDocument,
    require('../pages/ParentPage').parentDocument,
    require('../pages/PayrollPage').payrollDocument,
    require('../pages/PurchasePage').purchaseDocument,
    require('../pages/StudentPage').studentDocument,
    require('../pages/TimetablePage').timetableDocument,
    require('../pages/TimeOffPage').timeOffDocument,
    require('../pages/ToDosPage').toDosDocument,
  ];

  cachedPageDocuments = sourceDocuments.map((document) => {
    const sharedDocument = documentsBySlug[document.slug] || {};

    return {
      ...sharedDocument,
      ...document,
      category: sharedDocument.category || document.category,
      icon: sharedDocument.icon || document.icon,
    };
  });

  return cachedPageDocuments;
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getSearchTokens(value) {
  const normalized = normalizeSearchText(value);
  return normalized ? normalized.split(/\s+/) : [];
}

function createEntry(label, text, anchorId = null) {
  if (!text) {
    return null;
  }

  return {
    label,
    text: String(text).trim(),
    anchorId,
    normalizedLabel: normalizeSearchText(label),
    normalizedText: normalizeSearchText(text),
  };
}

function collectFigureEntries(figure, label, anchorId) {
  if (!figure) {
    return [];
  }

  return [
    createEntry(label, figure.alt, anchorId),
    createEntry(label, figure.caption, anchorId),
  ].filter(Boolean);
}

function collectGalleryEntries(items = [], label, anchorId) {
  return items.flatMap((item) =>
    [
      createEntry(label, item.alt, anchorId),
      createEntry(label, item.caption, anchorId),
    ].filter(Boolean)
  );
}

function collectDetailCardEntries(items = [], parentTrail, anchorId) {
  return items.flatMap((item) => {
    const label = [...parentTrail, item.title].join(' > ');
    return [
      createEntry(label, item.title, anchorId),
      ...(item.body || []).map((text) => createEntry(label, text, anchorId)),
      ...(item.bullets || []).map((text) => createEntry(label, text, anchorId)),
    ].filter(Boolean);
  });
}

function collectSectionEntries(section, trail = []) {
  const currentTrail = [...trail, section.title];
  const label = currentTrail.join(' > ');

  return [
    createEntry(label, section.title, section.id),
    ...(section.body || []).map((text) => createEntry(label, text, section.id)),
    ...(section.steps || []).map((text) => createEntry(label, text, section.id)),
    ...(section.bullets || []).map((text) => createEntry(label, text, section.id)),
    ...collectFigureEntries(section.figure, label, section.id),
    ...collectGalleryEntries(section.gallery, label, section.id),
    ...collectDetailCardEntries(section.detailCards, currentTrail, section.id),
    ...(section.subsections || []).flatMap((subsection) =>
      collectSectionEntries(subsection, currentTrail)
    ),
  ].filter(Boolean);
}

function getDocumentEntries(document) {
  return [
    createEntry('Module', document.title),
    createEntry('Overview', document.description),
    ...(document.metrics || []).flatMap((metric) =>
      [createEntry('Metrics', metric.label), createEntry('Metrics', metric.value)].filter(Boolean)
    ),
    ...(document.workflow || []).map((text) => createEntry('Workflow', text)),
    ...(document.sections || []).flatMap((section) => collectSectionEntries(section)),
  ].filter(Boolean);
}

function summarizeText(text, maxLength = 120) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();

  if (clean.length <= maxLength) {
    return clean;
  }

  return `${clean.slice(0, maxLength - 3).trimEnd()}...`;
}

function scoreEntry(entry, normalizedQuery, tokens) {
  const combined = `${entry.normalizedLabel} ${entry.normalizedText}`.trim();

  if (!tokens.every((token) => combined.includes(token))) {
    return -1;
  }

  let score = 0;

  if (entry.normalizedText.includes(normalizedQuery)) {
    score += 6;
  }

  if (entry.normalizedLabel.includes(normalizedQuery)) {
    score += 4;
  }

  if (tokens.every((token) => entry.normalizedText.includes(token))) {
    score += 3;
  }

  score += Math.max(0, 3 - Math.floor(entry.normalizedText.length / 80));

  return score;
}

function getBestDocumentMatch(document, query) {
  const tokens = getSearchTokens(query);
  const normalizedQuery = normalizeSearchText(query);

  if (!tokens.length) {
    return null;
  }

  const bestEntry = getDocumentEntries(document)
    .map((entry) => ({
      ...entry,
      score: scoreEntry(entry, normalizedQuery, tokens),
    }))
    .filter((entry) => entry.score >= 0)
    .sort((left, right) => right.score - left.score)[0];

  if (!bestEntry) {
    return null;
  }

  return {
    ...document,
    matchScore: bestEntry.score,
    matchAnchorId: bestEntry.anchorId,
    matchLabel: bestEntry.label,
    matchRoute: bestEntry.anchorId
      ? `${document.route}#${bestEntry.anchorId}`
      : document.route,
    matchText: bestEntry.text,
    matchSnippet: summarizeText(bestEntry.text),
  };
}

export function searchPortalDocuments(query) {
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) {
    return [];
  }

  return getPageDocuments()
    .map((document) => getBestDocumentMatch(document, normalizedQuery))
    .filter(Boolean)
    .sort((left, right) => right.matchScore - left.matchScore || left.title.localeCompare(right.title));
}
